import React, { useCallback, useMemo, useState } from "react";

import { FormattedMessage } from "react-intl";

import { EmptyMetrics } from "src/organization/pages/project/environment/metrics/components/Errors";
import HostSelector from "src/organization/pages/project/environment/metrics/components/HostSelector";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import Service from "src/organization/pages/project/environment/metrics/containers/Service";
import ContainerSkeleton from "src/organization/pages/project/environment/metrics/containers/Service/Service.skeleton";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useHosts from "src/organization/pages/project/environment/metrics/hooks/useHosts";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";

import * as S from "../grid/Grid.styles";
import { DG3_QUERY } from "../settings";

import useDedicatedPoll from "./useDedicated/useDedicatedPoll";

const Skeleton = () =>
  [1, 2, 3].map(value => <ContainerSkeleton key={value} />);

const getQuery = collection => ({
  fields: DG3_QUERY,
  stream: {
    collection,
    stream: "metrics"
  }
});

const Dedicated = ({ chorusURL, collection }) => {
  const [isDeployment, toggleDeployment] = useActivityToggle();
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>(
    useInitialTimeframe()
  );

  const pollInterval = getPollInterval(timeFrame);
  const query = useMemo(() => getQuery(collection), [collection]);
  const { data, status, lastUpdated } = useDedicatedPoll({
    url: chorusURL,
    timeFrame,
    query
  });
  const { result, hosts } = data || {};
  const dedicatedHost = useHosts(hosts);

  const [selectedHosts, setHosts] = dedicatedHost ?? [undefined, undefined];

  const onBrush = useCallback(
    timeFrame => setTimeFrame(timeFrame),
    [setTimeFrame]
  );

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeFrame: setTimeFrame,
    timeFrame: timeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };

  const isLoading = status.isLoading;

  const isEmpty = useMemo(
    () => result?.every(({ metrics }) => !metrics),
    [result]
  );

  const stickyRef = useStickyTimecontrol();

  return (
    <MetricsContext.Provider value={{ timeFrame, isDeployment, setTimeFrame }}>
      <V.Header ref={stickyRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.grid.heading" />
        </V.Heading>
        <TimeframeControl
          timeframe={{
            timeframe: timeFrame,
            onChange: setTimeFrame
          }}
          isUpdating={isLoading}
          pollInterval={pollInterval}
          lastUpdated={lastUpdated}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
          deployment={{
            enabled: isDeployment,
            onChange: toggleDeployment
          }}
        >
          <HostSelector
            hosts={hosts}
            selected={selectedHosts}
            onChange={setHosts}
          />
        </TimeframeControl>
      </V.Header>
      <S.Layout>
        {isLoading && !result && <Skeleton />}
        {!isLoading && isEmpty && <EmptyMetrics />}
        {result?.map((service, i) => (
          <Service
            key={i}
            service={service}
            onBrush={onBrush}
            hosts={selectedHosts}
            isLoading={status.refresh}
            kind="dedication"
          />
        ))}
      </S.Layout>
    </MetricsContext.Provider>
  );
};

export default Dedicated;
